import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Lock, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function VaultPin() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [pinSet, setPinSet] = useState(null); // null = loading
  const [stage, setStage] = useState("enter"); // enter | set | confirm
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [confirmDigits, setConfirmDigits] = useState(["", "", "", ""]);
  const [shake, setShake] = useState(false);
  const inputRefs = useRef([]);
  const confirmRefs = useRef([]);

  useEffect(() => {
    api.get("/vault/status")
      .then(r => { const ps = r.data.pin_set; setPinSet(ps); setStage(ps ? "enter" : "set"); })
      .catch(e => toast.error(fmtErr(e)));
  }, []);

  const setDigit = (idx, val, isConfirm = false) => {
    if (!/^\d?$/.test(val)) return;
    const arr = isConfirm ? [...confirmDigits] : [...digits];
    arr[idx] = val;
    isConfirm ? setConfirmDigits(arr) : setDigits(arr);
    if (val) {
      const next = (isConfirm ? confirmRefs : inputRefs).current[idx + 1];
      if (next) next.focus();
    }
  };

  const onKey = (e, idx, isConfirm = false) => {
    if (e.key === "Backspace") {
      const arr = isConfirm ? [...confirmDigits] : [...digits];
      if (!arr[idx] && idx > 0) {
        const prev = (isConfirm ? confirmRefs : inputRefs).current[idx - 1];
        if (prev) prev.focus();
      }
    }
  };

  // Auto-submit when digits fill. `submit`/`submitSet` are recreated every render
  // (they close over `digits`/`confirmDigits`); adding them as deps would create
  // an infinite loop. Instead we mirror the latest version into a ref and read
  // it inside the effect — this is the standard "Effect Event" pattern.
  const submitRef = useRef(null);
  const submitSetRef = useRef(null);

  useEffect(() => {
    if (digits.every(d => d) && stage === "enter") submitRef.current?.();
    if (digits.every(d => d) && stage === "set") setStage("confirm");
    if (confirmDigits.every(d => d) && stage === "confirm") submitSetRef.current?.();
  }, [digits, confirmDigits, stage]);

  const submit = async () => {
    try {
      // Backend sets HttpOnly `vault_session` cookie on success. No token
      // ever touches JS — we just navigate on success.
      await api.post("/vault/pin/verify", { pin: digits.join("") });
      nav("/vault");
    } catch (e) {
      setShake(true); setTimeout(()=>setShake(false), 400);
      setDigits(["", "", "", ""]); inputRefs.current[0]?.focus();
      toast.error(fmtErr(e));
    }
  };

  const submitSet = async () => {
    if (digits.join("") !== confirmDigits.join("")) {
      setShake(true); setTimeout(()=>setShake(false), 400);
      setConfirmDigits(["", "", "", ""]); confirmRefs.current[0]?.focus();
      toast.error(t("vault.pinsNoMatch"));
      return;
    }
    try {
      await api.post("/vault/pin/set", { pin: digits.join("") });
      toast.success(t("vault.pinSetToast"));
      nav("/vault");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  // Keep refs in sync with the latest closures so the auto-submit effect above
  // can always call the freshest version without listing them as deps.
  submitRef.current = submit;
  submitSetRef.current = submitSet;

  const reset = async () => {
    if (!window.confirm(t("vault.forgotConfirm"))) return;
    await api.post("/vault/pin/reset");
    toast(t("vault.vaultWiped"));
    setPinSet(false); setStage("set"); setDigits(["", "", "", ""]);
    inputRefs.current[0]?.focus();
  };

  if (pinSet == null) return <div className="min-h-screen bg-black text-zinc-500 flex items-center justify-center">…</div>;

  const title = stage === "enter" ? t("vault.pinEnter") : stage === "set" ? t("vault.pinSet") : t("vault.pinConfirm");
  const showDigits = stage === "confirm" ? confirmDigits : digits;
  const refsToUse = stage === "confirm" ? confirmRefs : inputRefs;
  const isConfirmStage = stage === "confirm";

  return (
    <div className="min-h-screen bg-black text-white relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(230,0,0,0.15)_0%,transparent_60%)] pointer-events-none" />
      <div className="relative max-w-md mx-auto px-6 pt-6 pb-20">
        <div className="flex items-center justify-between" data-testid="pin-header">
          <Link to="/home" className="flex items-center gap-2 text-zinc-300 hover:text-white" data-testid="pin-back">
            <ArrowLeft size={18} className="rtl:rotate-180"/> <span>{t("vault.back")}</span>
          </Link>
          <span className="font-mono-accent text-zinc-400">{t("vault.secureVault")}</span>
          <span className="w-12" />
        </div>

        <div className="mt-24 flex flex-col items-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#E60000] to-[#660000] flex items-center justify-center shadow-[0_0_40px_rgba(230,0,0,0.6)]">
            <Lock size={32} className="text-white" />
          </div>
          <h1 className="mt-8 text-3xl font-bold text-center" style={{ fontFamily: "'Manrope',sans-serif", fontWeight: 800 }} data-testid="pin-title">
            {title}
          </h1>
          <p className="mt-3 font-mono-accent text-zinc-500 text-center">{t("vault.pinSecure")}</p>

          <div className={`mt-12 flex gap-4 ${shake ? "animate-shake" : ""}`} data-testid="pin-slots">
            {[0, 1, 2, 3].map(i => (
              <input
                key={`${stage}-${i}`}
                ref={el => refsToUse.current[i] = el}
                inputMode="numeric"
                maxLength={1}
                value={showDigits[i]}
                onChange={(e) => setDigit(i, e.target.value, isConfirmStage)}
                onKeyDown={(e) => onKey(e, i, isConfirmStage)}
                data-testid={`pin-slot-${i}`}
                className="w-16 h-20 rounded-2xl border border-[#E60000]/30 bg-black/60 text-center text-3xl font-bold text-white outline-none focus:border-[#E60000] focus:shadow-[0_0_20px_rgba(230,0,0,0.3)] transition"
              />
            ))}
          </div>

          <p className="mt-6 font-mono-accent text-zinc-500 text-[0.7rem] text-center">
            {t("vault.pinTap")}
          </p>

          {stage === "enter" && (
            <button onClick={reset} data-testid="pin-forgot" className="mt-12 text-xs font-mono-accent text-zinc-500 hover:text-[#E60000] transition">
              {t("vault.pinForgot")}
            </button>
          )}
        </div>

        <p className="absolute bottom-6 inset-x-0 text-center font-mono-accent text-zinc-700 text-[0.65rem]">{t("vault.pinE2E")}</p>
      </div>

      <style>{`
        @keyframes shake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-8px)} 75%{transform:translateX(8px)} }
        .animate-shake { animation: shake 0.4s ease; }
      `}</style>
    </div>
  );
}
